// frontend/src/utils/appEvents.ts
/**
 * Typed application event bus.
 *
 * The app coordinates a handful of cross-tree signals over `window`
 * CustomEvents (components that are siblings in the provider tree, or a
 * non-React module such as the API client talking to a React context).
 * Those event names used to be string literals scattered across dispatch and
 * listener sites, with untyped `detail` payloads and `as EventListener` casts.
 *
 * This module is the single source of truth for those channels: one place
 * holds each event name, its payload type, and the dispatch/subscribe pair.
 *
 * `subscribe` returns an unsubscribe function, so a listener is a one-liner
 * inside `useEffect`:
 *
 *   useEffect(() => subscribeToDegreeAuditRefresh(({ planId }) => { ... }), [...]);
 */

/** Payload of the `askChatbotHelp` event. */
export interface AskChatbotHelpDetail {
  /** The message to send to the chat assistant on the user's behalf. */
  message: string;
  /** Coarse action label used for analytics / assistant routing. */
  action: string;
  /** Requirement context, when the request originates from the degree audit. */
  requirementName?: string | null;
  /** Program context, when the requirement belongs to a declared program. */
  programName?: string | null;
}

/** Payload of the `refreshDegreeAudit` event. */
export interface DegreeAuditRefreshDetail {
  /** Plan whose audit went stale; listeners scoped to another plan ignore it. */
  planId: number;
}

/** Payload of the `auth:signout-required` event. */
export interface SignoutRequiredDetail {
  /** Why the signout was forced (e.g. `invalid_refresh_token`). */
  reason: string;
}

/** Payload of the `terms:required` event. */
export interface TermsRequiredDetail {
  /** The Claude surface that was refused (e.g. `chat`). */
  surface: string;
}

interface AppEventChannel<TDetail> {
  /** The underlying `window` event name — exported for tests and debugging. */
  readonly type: string;
  dispatch: (detail: TDetail) => void;
  /** Registers a listener; returns the matching unsubscribe function. */
  subscribe: (handler: (detail: TDetail) => void) => () => void;
}

const createChannel = <TDetail>(type: string): AppEventChannel<TDetail> => ({
  type,
  dispatch: (detail: TDetail): void => {
    window.dispatchEvent(new CustomEvent<TDetail>(type, { detail }));
  },
  subscribe: (handler: (detail: TDetail) => void): (() => void) => {
    const listener = (event: Event): void => {
      handler((event as CustomEvent<TDetail>).detail);
    };
    window.addEventListener(type, listener);
    return () => window.removeEventListener(type, listener);
  },
});

const askChatbotHelpChannel = createChannel<AskChatbotHelpDetail>('askChatbotHelp');
const degreeAuditRefreshChannel = createChannel<DegreeAuditRefreshDetail>('refreshDegreeAudit');
const signoutRequiredChannel = createChannel<SignoutRequiredDetail>('auth:signout-required');
const termsRequiredChannel = createChannel<TermsRequiredDetail>('terms:required');

/** Event names, for tests and for the rare site that needs the raw string. */
export const APP_EVENTS = {
  askChatbotHelp: askChatbotHelpChannel.type,
  refreshDegreeAudit: degreeAuditRefreshChannel.type,
  signoutRequired: signoutRequiredChannel.type,
  termsRequired: termsRequiredChannel.type,
} as const;

/** A Claude surface was refused with `terms_required`; AppContext re-syncs. */
export const dispatchTermsRequired = (detail: TermsRequiredDetail): void =>
  termsRequiredChannel.dispatch(detail);

/** Listen for `terms_required` refusals (AppContext is the only listener). */
export const subscribeToTermsRequired = (
  handler: (detail: TermsRequiredDetail) => void
): (() => void) => termsRequiredChannel.subscribe(handler);

/** Ask the chat assistant for help on the user's behalf. */
export const dispatchAskChatbotHelp = (detail: AskChatbotHelpDetail): void =>
  askChatbotHelpChannel.dispatch(detail);

/** Listen for chat-help requests (the chat column is the only listener). */
export const subscribeToAskChatbotHelp = (
  handler: (detail: AskChatbotHelpDetail) => void
): (() => void) => askChatbotHelpChannel.subscribe(handler);

/** Tell the degree audit its data for `planId` is stale. */
export const dispatchDegreeAuditRefresh = (planId: number): void =>
  degreeAuditRefreshChannel.dispatch({ planId });

/** Listen for degree-audit refresh requests. */
export const subscribeToDegreeAuditRefresh = (
  handler: (detail: DegreeAuditRefreshDetail) => void
): (() => void) => degreeAuditRefreshChannel.subscribe(handler);

/** Ask AuthContext to sign the user out (dispatched from non-React modules). */
export const dispatchSignoutRequired = (detail: SignoutRequiredDetail): void =>
  signoutRequiredChannel.dispatch(detail);

/** Listen for forced-signout requests. */
export const subscribeToSignoutRequired = (
  handler: (detail: SignoutRequiredDetail) => void
): (() => void) => signoutRequiredChannel.subscribe(handler);
